import { Avatar, Box, Button, Divider, Drawer, Grid, Paper, Stack, TextField, Typography } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { RealInput } from "../../../core/components/RealInput";
import { useSnackbar } from "../../../core/contexts/SnackbarContext";
import { useMoeda } from "../../../core/hooks/useMoeda";
import { IItemProduto } from "../../../pedidos/interface/IItemProduto";
import { IProdutoPedido } from "../../../pedidos/interface/IProdutoPedido";
import { calcularPrecoComplemento } from "../../../pedidos/utils/calcularPrecoComplemento";
import { IComplemento } from "../../interfaces/IComplemento";
import { IProduto } from "../../interfaces/IProduto";
import { BodyDetalhesContainer, DetalhesContainer, HeaderDetalhesContainer } from "../CardPreparadoOperacao/styles";
import { ComplementoPaperInput } from "./container/ComplementoPaperInput";
import { IITemComplemento } from "../../interfaces/IITemComplemento";
import { v4 } from "uuid";
import { useTamanhoTela } from "../../../core/hooks/useTamanhoTela";
import { ChevronLeft } from "@mui/icons-material";

interface DrawerPreparadoOperacaoProps {
    open: boolean;
    toggleDrawer: () => void;
    produto: IProduto | IProdutoPedido;
    onSubmit: (produto: IProdutoPedido) => void;

    complementos?: IComplemento[];
    observacao?: string;
    valorAdicional?: any;
    uuid?: string;
}

export const DrawerPreparadoOperacao = ({
    toggleDrawer,
    onSubmit,
    produto,
    open,
    uuid,
    observacao: observacaoEdicao,
    valorAdicional: valorAdicionalEdicao,
    complementos: complementosEdicao
}: DrawerPreparadoOperacaoProps) => {
    const [complementos, setComplementos] = useState<IComplemento[]>(complementosEdicao ?? (produto.complementos ?? []));
    const [observacao, setObservacao] = useState<string | undefined>(observacaoEdicao);
    const [valorAdicional, setValorAdicional] = useState<any>(valorAdicionalEdicao);
    const { modeloTela } = useTamanhoTela();

    const snackbar = useSnackbar();
    const { real } = useMoeda();

    const buttonRef = useRef<HTMLButtonElement>(null);
    const normalizadoRef = useRef<boolean>(false);

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (open && buttonRef.current && modeloTela === "desktop") {
                buttonRef.current.focus();
            }
        }, 500);

        return () => clearTimeout(timeout);
    }, [open]);

    // Normalizar quantidades dos itens selecionados ao abrir o drawer
    // Garantir que a quantidade da ficha técnica não seja usada como quantidade do item no pedido
    useEffect(() => {
        if (!open) {
            normalizadoRef.current = false;
            return;
        }
        
        if (normalizadoRef.current || !complementos) return;
        
        let precisaAtualizar = false;
        const complementosNormalizados = complementos.map(complemento => {
            const itensNormalizados = complemento.itensSelecionados?.map(item => {
                // Se o item não tem quantidade definida ou tem quantidade 0, definir como 1
                // Isso garante que a quantidade da ficha técnica não seja usada
                if (!item.quantidade || item.quantidade <= 0) {
                    precisaAtualizar = true;
                    return {
                        ...item,
                        quantidade: 1
                    };
                }
                return item;
            }) ?? [];
            
            if (itensNormalizados.length !== (complemento.itensSelecionados?.length ?? 0)) {
                precisaAtualizar = true;
            }
            
            return {
                ...complemento,
                itensSelecionados: itensNormalizados
            };
        });
        
        if (precisaAtualizar) {
            normalizadoRef.current = true;
            setComplementos(complementosNormalizados);
        } else {
            normalizadoRef.current = true;
        }
    }, [open, complementos]);

    const handleValorAdicional = (event: React.ChangeEvent<HTMLInputElement>) => {
        setValorAdicional(Number(event.target.value))
    }

    const handleObservacao = (event: React.ChangeEvent<HTMLInputElement>) => {
        setObservacao(event.target.value)
    }

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const erros: string[] = [];

        complementos.map(complemento => {
            if (complemento.obrigatorio && (!complemento.itensSelecionados || complemento.itensSelecionados?.length == 0)) {
                erros.push(`O complemento ${complemento.descricao} não foi preenchido`)
            }

            if (complemento.obrigatorio && ((complemento.itensSelecionados?.length ?? 1) < complemento.numeroMinimoItens)) {
                erros.push(`O complemento ${complemento.descricao} precisa de ${complemento.numeroMinimoItens} itens selecionados`);
            }

            if (complemento.obrigatorio && complemento.numeroMaximoItens && ((complemento.itensSelecionados?.length ?? 1) > complemento.numeroMaximoItens)) {
                erros.push(`O complemento ${complemento.descricao} precisa de ${complemento.numeroMaximoItens} itens selecionados`);
            }
        })

        if (erros.length) {
            return snackbar(erros[0], { severity: "error" })
        }

        const complementosComQuantidade = complementos.map(complemento => ({
            ...complemento,
            itensSelecionados: complemento.itensSelecionados?.map(item => ({
                ...item,
                quantidade: item.quantidade ?? 1
            })) ?? []
        }));

        const dadosProduto: IProdutoPedido = {
            uuidProdutoReferencia: produto.uuid,
            valorAdicional,
            ...produto,
            observacao,
            complementos: complementosComQuantidade,
            uuid: uuid ?? v4()
        }

        onSubmit(dadosProduto);
        handleToggleDrawer();
    }


    const definirItensComplemento = (complementoUuid: string, itens: IITemComplemento[]) => {
        const novosComplementos = complementos;
        const indexComplemento = complementos.findIndex(complemento => complemento.uuid === complementoUuid);

        if (indexComplemento == -1) {
            return snackbar("Complemento não encontrado");
        }

        novosComplementos[indexComplemento].itensSelecionados = itens;
        setComplementos([...novosComplementos])
    }

    const handleToggleDrawer = () => {
        if (!uuid) {
            const complementos = produto.complementos?.map(complemento => {
                complemento.itensSelecionados = []
                return complemento;
            }) ?? [];
            setComplementos(complementos);
            setObservacao(undefined);
            setValorAdicional(undefined);
        }

        toggleDrawer();
    }

    const ajustarQuantidade = (complementoUuid: string, item: IITemComplemento, delta: number) => {
        const novosComplementos = [...complementos];
        const indexComplemento = novosComplementos.findIndex(c => c.uuid === complementoUuid);

        if (indexComplemento === -1) return snackbar("Complemento não encontrado");

        const complemento = novosComplementos[indexComplemento];
        const indexItem = complemento.itensSelecionados?.findIndex(i => i.uuid === item.uuid);

        if (indexItem === undefined || indexItem < 0) return;

        const itemAtual = complemento.itensSelecionados![indexItem];
        const novaQuantidade = (itemAtual.quantidade ?? 1) + delta;

        if (novaQuantidade <= 0) {
            complemento.itensSelecionados!.splice(indexItem, 1);
        } else {
            complemento.itensSelecionados![indexItem] = {
                ...itemAtual,
                quantidade: novaQuantidade,
            };
        }

        setComplementos([...novosComplementos]);
    };


    const valorTotal = useMemo(() => {
        let total = produto.preco ?? 0;

        complementos?.map(complemento => {
            total = total + calcularPrecoComplemento(complemento);
        })

        total = total + (valorAdicional ?? 0);

        return total ?? produto.preco;
    }, [complementos, valorAdicional, uuid, produto.preco])


    return (
        <Drawer
            anchor="bottom"
            open={open}
            onClose={handleToggleDrawer}
            sx={{ borderRadius: 0, zIndex: 10002 }}
        >
            <form onSubmit={handleSubmit}>
                <DetalhesContainer>
                    {modeloTela != "desktop" ? (
                        <Button onClick={handleToggleDrawer} sx={{ 
                            height: "70px",
                            width: '100%',
                            marginBottom: '2rem'
                        }} startIcon={<ChevronLeft />}>Voltar</Button>
                    ) : ''}
                    <HeaderDetalhesContainer>
                        {produto.imagens?.length ? (
                            <Avatar
                                alt="Preparado"
                                src={`${produto.imagens[0].url}`}
                                sx={{ width: 90, height: 90 }}
                            />
                        ) : (
                            <Avatar
                                alt="Preparado"
                                src={`${process.env.PUBLIC_URL}/images/produtos/generico.png`}
                                sx={{ width: 90, height: 90 }}
                            />
                        )}
                        <div>
                            <Typography gutterBottom variant="h5" fontWeight={"bold"} component="div">
                                {produto.descricao}
                            </Typography>
                            <Typography gutterBottom variant="h6" fontWeight={"bold"} component="div">
                                {real(valorTotal)}
                            </Typography>
                        </div>
                    </HeaderDetalhesContainer>
                    <BodyDetalhesContainer>
                        <Grid
                            spacing={1}
                            container
                            height={"100%"}
                        >
                            <Grid item xs={12} sm={6}>
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        padding: "1rem",
                                        height: "100%",
                                        overflowY: "auto",
                                        gap: "1rem",
                                        display: "flex",
                                        flexDirection: "column"
                                    }}
                                >
                                    <Typography gutterBottom variant="h6" fontWeight={"bold"} component="div">
                                        Detalhes
                                    </Typography>
                                    {complementos?.map(complemento => {
                                        return (
                                            <ComplementoPaperInput definirItensComplemento={definirItensComplemento} complemento={complemento} />
                                        );
                                    })}
                                    {produto.configuracao?.habilitarObservacao && (
                                        <Box
                                            sx={{
                                                width: "100%",
                                                padding: "1rem",
                                                display: "flex",
                                                flexDirection: "column",
                                                justifyContent: "center",
                                                alignItems: "center"
                                            }}
                                        >
                                            <Stack width={"100%"} flexDirection={"row"} justifyContent={"space-between"}>
                                                <Typography gutterBottom variant="h6" component="div">
                                                    Observação
                                                </Typography>
                                                <Typography gutterBottom variant="h6" fontWeight={"bold"} component="div">
                                                    {valorAdicional ? real(valorAdicional) : "R$ 0,00"}
                                                </Typography>
                                            </Stack>
                                            <Box sx={{ width: "100%", justifyContent: "center", display: "flex" }}>
                                                <TextField
                                                    sx={{ width: '65%' }}
                                                    label="Observação"
                                                    placeholder="Observação"
                                                    onChange={handleObservacao}
                                                    value={observacao}
                                                />
                                                <TextField
                                                    sx={{ width: '35%', marginLeft: "0.5rem" }}
                                                    label="Valor adicional"
                                                    placeholder="Valor adicional"
                                                    onChange={handleValorAdicional}
                                                    value={valorAdicional}
                                                    InputProps={{
                                                        inputComponent: RealInput as any,
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                    )}
                                </Paper>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Paper variant="outlined" sx={{ padding: "1rem", height: "100%", display: "flex", flexDirection: "column" }}>
                                    <Typography gutterBottom variant="h6" fontWeight="bold">
                                        Resumo
                                    </Typography>

                                    <Box sx={{ flex: 1, overflowY: "auto", mt: 1 }}>
                                        {complementos.map((complemento) => (
                                            <Box key={complemento.uuid} sx={{ marginBottom: 2 }}>
                                                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                                                    {complemento.descricao}
                                                </Typography>

                                                {complemento.itensSelecionados?.map((item) => (
                                                    <Box
                                                        key={item.uuid}
                                                        sx={{
                                                            display: "flex",
                                                            justifyContent: "space-between",
                                                            alignItems: "center",
                                                            py: 1,
                                                            flexWrap: "wrap",
                                                        }}
                                                    >
                                                        <Box sx={{ flex: 1, minWidth: "60%" }}>
                                                            <Typography>{item.descricao}</Typography>
                                                            <Typography fontWeight="bold">
                                                                {real(item.preco * (item.quantidade ?? 1))}
                                                            </Typography>
                                                        </Box>

                                                        <Stack direction="row" spacing={1} alignItems="center">
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                onClick={() => ajustarQuantidade(complemento.uuid, item, -1)}
                                                                sx={{
                                                                    minWidth: 32,
                                                                    height: 32,
                                                                    borderRadius: "50%",
                                                                    fontWeight: "bold",
                                                                    padding: 0,
                                                                }}
                                                            >
                                                                -
                                                            </Button>
                                                            <Typography>{item.quantidade ?? 1}</Typography>
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                onClick={() => ajustarQuantidade(complemento.uuid, item, 1)}
                                                                sx={{
                                                                    minWidth: 32,
                                                                    height: 32,
                                                                    borderRadius: "50%",
                                                                    fontWeight: "bold",
                                                                    padding: 0,
                                                                }}
                                                            >
                                                                +
                                                            </Button>
                                                        </Stack>
                                                    </Box>
                                                ))}
                                            </Box>
                                        ))}

                                        {produto.configuracao?.habilitarObservacao && (
                                            <Box sx={{ mt: 2 }}>
                                                <Typography variant="subtitle1" fontWeight="bold">
                                                    Observação
                                                </Typography>
                                                {observacao && <Typography>{observacao}</Typography>}
                                                {valorAdicional > 0 && (
                                                    <Typography fontWeight="bold">
                                                        {real(valorAdicional)}
                                                    </Typography>
                                                )}
                                            </Box>
                                        )}
                                    </Box>
                                    <Button
                                        sx={{ marginTop: "1rem", width: "100%", height: "5rem" }}
                                        type="submit"
                                        variant="contained"
                                        ref={buttonRef}
                                    >
                                        CONCLUIR
                                    </Button>
                                </Paper>
                            </Grid>
                        </Grid>
                    </BodyDetalhesContainer>
                </DetalhesContainer>
            </form>
        </Drawer>
    );
}